import { useMemo, useState } from "react";
import { cn } from "@nexirt/ui";

/**
 * CalendarPanel — Dynamic Output mock for "Book a 30-min discovery
 * call." Rev 25.3 swaps the static cell array for a real month grid
 * driven by Newton's actual availability:
 *
 *   Available: Sun, Mon, Tue, Wed, Sat (getDay 0,1,2,3,6)
 *   Closed:    Thu, Fri (getDay 4,5)
 *   Hours:     9:00 AM – 4:30 PM, 30-minute slots
 *
 * Past days render dim + aria-disabled. Thu/Fri render dim +
 * aria-disabled with cursor-not-allowed. Selecting a slot dispatches
 * 'nexirt:copilot:open' with a pre-formed booking message — the
 * ChatWidget opens with the prompt loaded so the visitor can confirm
 * (or edit) and send. Newton replies personally within one business
 * day. Phase 4 swaps the event handler for a real provider (Cal.com /
 * Calendly / custom — owner pending).
 */

const DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const AVAILABLE = new Set([0, 1, 2, 3, 6]); // Sun, Mon, Tue, Wed, Sat

const SLOTS = [
  "9:00 AM", "9:30 AM",
  "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM",
  "1:00 PM", "1:30 PM",
  "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM",
  "4:00 PM", "4:30 PM",
];

type Cell = {
  date: Date;
  inMonth: boolean;
  isPast: boolean;
  isAvailable: boolean;
  label: string;
};

function buildMonthGrid(monthAnchor: Date): Cell[] {
  const year = monthAnchor.getFullYear();
  const month = monthAnchor.getMonth();
  const first = new Date(year, month, 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Monday-first grid: getDay() returns Sun=0, so shift so Mon=0.
  const firstDow = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - firstDow);
  const cells: Cell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const inMonth = d.getMonth() === month;
    const isPast = d < today;
    const isAvailable = !isPast && AVAILABLE.has(d.getDay());
    cells.push({ date: d, inMonth, isPast, isAvailable, label: String(d.getDate()) });
  }
  // Trim trailing all-out-of-month rows to keep 5 or 6 rows.
  while (cells.length > 35 && cells.slice(-7).every((c) => !c.inMonth)) {
    cells.length -= 7;
  }
  return cells;
}

function formatDayLong(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function monthYearLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export function CalendarPanel() {
  const [monthAnchor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const cells = useMemo(() => buildMonthGrid(monthAnchor), [monthAnchor]);

  // First selectable cell becomes the default — gives the slot column
  // immediate substance on mount instead of an empty placeholder.
  const defaultSelected = cells.find((c) => c.inMonth && c.isAvailable) ?? null;
  const [selectedIdx, setSelectedIdx] = useState<number | null>(
    defaultSelected ? cells.indexOf(defaultSelected) : null,
  );
  const selected = selectedIdx != null ? cells[selectedIdx] ?? null : null;

  function pickSlot(time: string) {
    if (!selected) return;
    const when = `${formatDayLong(selected.date)} at ${time}`;
    const prompt = `I'd like to book a 30-min discovery call with one of your engineers on ${when}.`;
    window.dispatchEvent(
      new CustomEvent("nexirt:copilot:open", { detail: { prompt } }),
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
      <div
        className="rounded-nx2 border border-nx-line bg-nx-surface/60 p-4"
        data-calendar-panel
      >
        <h4 className="font-display text-base font-semibold text-nx-text">
          Discovery call · 30 min
        </h4>
        <div className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[0.14em] text-nx-text-faint">
          {monthYearLabel(monthAnchor)} · NEXIRT engineering team
        </div>
        <div
          className="mt-3 grid grid-cols-7 gap-1 text-center text-[11px]"
          role="grid"
          aria-label="Discovery call availability"
        >
          {DOW.map((d) => (
            <div
              key={d}
              className="font-mono text-[10px] uppercase tracking-[0.12em] text-nx-text-faint"
            >
              {d}
            </div>
          ))}
          {cells.map((c, i) => {
            const isSelected = selectedIdx === i;
            const dow = c.date.getDay();
            const isWeekendClosed = !c.isPast && (dow === 4 || dow === 5);
            const clickable = c.isAvailable;
            return (
              <button
                key={i}
                type="button"
                role="gridcell"
                onClick={clickable ? () => setSelectedIdx(i) : undefined}
                aria-disabled={!clickable}
                aria-selected={isSelected}
                aria-label={c.date.toDateString() + (clickable ? "" : " (unavailable)")}
                disabled={!clickable}
                data-calendar-day-dow={dow}
                data-calendar-day-state={
                  isSelected
                    ? "selected"
                    : !c.inMonth
                      ? "muted"
                      : c.isPast
                        ? "past"
                        : isWeekendClosed
                          ? "closed"
                          : clickable
                            ? "available"
                            : "muted"
                }
                className={cn(
                  "grid h-8 place-items-center rounded-[6px] transition-colors",
                  isSelected && "cursor-pointer bg-nx-accent text-white shadow-[var(--nx-shadow-glow)]",
                  !isSelected && !c.inMonth && "text-nx-text-faint/40 cursor-default",
                  !isSelected && c.inMonth && c.isPast && "text-nx-text-faint/50 cursor-not-allowed",
                  !isSelected && c.inMonth && !c.isPast && isWeekendClosed && "text-nx-text-faint/60 cursor-not-allowed",
                  !isSelected && c.inMonth && clickable && "cursor-pointer text-nx-text hover:bg-nx-accent-2-soft hover:text-nx-text",
                )}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        <p className="mt-3 font-mono text-[10.5px] text-nx-text-faint">
          Closed Thu &amp; Fri · all other days open 9 AM – 5 PM
        </p>
      </div>

      <div>
        <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-nx-text-faint">
          {selected ? `Available · ${formatDayLong(selected.date)}` : "Pick a day to see slots"}
        </div>
        <div className="mt-2 grid gap-1.5" data-calendar-slot-list>
          {selected
            ? SLOTS.map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => pickSlot(time)}
                  data-calendar-slot={time}
                  className={cn(
                    "flex w-full items-center justify-between rounded-nx2 border px-3 py-2 text-left text-[13px] transition-colors",
                    "border-nx-line bg-nx-surface/60 text-nx-text-dim hover:border-nx-accent hover:bg-nx-accent-soft hover:text-nx-text",
                  )}
                >
                  <span>{time}</span>
                  <span className="font-mono text-[10.5px] text-nx-text-faint">
                    30 min · with our team
                  </span>
                </button>
              ))
            : null}
        </div>
        <p className="mt-3 text-[11px] text-nx-text-faint">
          Times shown in your local timezone · An engineer replies within 1 business day.
        </p>
      </div>
    </div>
  );
}

export default CalendarPanel;
